import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { savePostLoginRedirect, clearPostLoginRedirectIfArrived } from '../lib/redirectByRole';

export default function ProtectedRoute({
  children,
  requireRole,
}: {
  children: React.ReactNode;
  requireRole?: 'emt' | 'employer';
}) {
  const { user, role, profile, loading, profileReady } = useAuth();
  const location = useLocation();
  const effectiveRole = (profile?.role as 'emt' | 'employer' | 'institute' | undefined) ?? role;

  // Hard-bypass public profile: never redirect away from /p/
  if (location.pathname.startsWith('/p/')) {
    return <>{children}</>;
  }

  if (loading) return <div className="p-8 text-center text-[var(--text-secondary)]">Loading…</div>;
  if (!user) {
    // Remember the real destination (e.g. a credentialing checklist link from an email) so
    // redirectByRole can send the visitor here after they log in, instead of always landing
    // on their role's default dashboard.
    savePostLoginRedirect(location.pathname + location.search);
    return <Navigate to="/" replace />;
  }

  // Arrived at the exact remembered destination while authenticated — the redirect has served
  // its purpose, clear it so it can't misfire on a later, unrelated login in this tab. (Landing
  // on some other protected route, e.g. the default dashboard, leaves it in place — redirectByRole
  // may still be racing to apply it.)
  clearPostLoginRedirectIfArrived(location.pathname + location.search);

  // A role-specific screen must never render for an account whose role is unknown (profile not
  // loaded, or a lapsed session that cleared it): wait while the profile is still resolving, and
  // send a truly role-less account to the landing page instead of showing the candidate app.
  // A signed-in account whose profile is still loading, or whose profile exists without a role,
  // must not be painted as the requested role's app. Never navigate from here (a brand-new signup
  // with NO profile yet must keep reaching its registration screens; AuthContext owns routing).
  if (requireRole && !effectiveRole && (!profileReady || profile)) {
    return (
      <div className="p-8 text-center text-[var(--text-secondary)]" data-testid="protected-route-resolving">
        {profileReady ? 'Your account has no role yet. Please sign in again.' : 'Resolving your account…'}
      </div>
    );
  }

  if (requireRole && effectiveRole && effectiveRole !== requireRole) {
    // Institute accounts never get EMT or employer app screens.
    if (effectiveRole === 'institute') {
      return <Navigate to="/institute/dashboard" replace />;
    }
    // Redirect to correct dashboard based on actual role
    if (effectiveRole === 'emt') {
      if (import.meta.env.DEV) {
        console.log('[GUARD_REDIRECT] ProtectedRoute (emt) => /dashboard from', location.pathname);
        console.trace('[GUARD_REDIRECT_TRACE]');
      }
      return <Navigate to="/dashboard" replace />;
    }
    if (effectiveRole === 'employer') {
      if (import.meta.env.DEV) {
        console.log('[GUARD_REDIRECT] ProtectedRoute (employer) => /employer/dashboard from', location.pathname);
        console.trace('[GUARD_REDIRECT_TRACE]');
      }
      return <Navigate to="/employer/dashboard" replace />;
    }
  }

  return <>{children}</>;
}

